import React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Typography from '@mui/joy/Typography';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import ListItemContent from '@mui/joy/ListItemContent';
import Divider from '@mui/joy/Divider';
import Chip from '@mui/joy/Chip';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderIcon from '@mui/icons-material/Folder';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';

import { themeColors, shadows } from '../../styles/theme';
import logo from '../../assets/logo.png';

export default function ModernSidebar({
  workspaces = [],
  selectedWorkspace,
  onWorkspaceSelect,
  onCreateWorkspace,
  onShowAllWorkspaces,
  collapsed = false,
  onToggleCollapse
}) {
  const navigate = useNavigate();
  const sidebarWidth = collapsed ? 72 : 280;

  return (
    <Box
      sx={{
        width: sidebarWidth,
        height: '100vh',
        backgroundColor: 'white',
        boxShadow: shadows.md,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 100,
      }}
    >
      {/* Logo & Toggle */}
      <Box
        sx={{
          p: 3,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          minHeight: 80,
        }}
      >
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img src={logo} alt="Vocative" style={{ height: 24, width: 'auto' }} />
          </Box>
        )}

        <IconButton
          size="sm"
          onClick={onToggleCollapse}
          sx={{
            backgroundColor: themeColors.neutral[100],
            border: 'none',
            boxShadow: shadows.sm,
            '&:hover': {
              backgroundColor: themeColors.primary[50],
              boxShadow: shadows.md,
              color: themeColors.primary[600],
            },
            transition: 'all 0.2s ease',
          }}
        >
          {collapsed ? <MenuIcon /> : <MenuOpenIcon />}
        </IconButton>
      </Box>

      {/* Navigation */}
      <Box sx={{ px: collapsed ? 1 : 2, mb: 2 }}>
        <List size="sm" sx={{ '--List-gap': '6px', '--ListItem-radius': '10px' }}>
          <ListItem>
            <Tooltip title={collapsed ? "All Workspaces" : ""} placement="right">
              <ListItemButton
                selected={!selectedWorkspace}
                onClick={onShowAllWorkspaces}
                sx={{
                  py: 1.5,
                  border: 'none',
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  '&.Joy-selected': {
                    backgroundColor: themeColors.primary[50],
                    color: themeColors.primary[700],
                    boxShadow: shadows.sm,
                    '&:hover': {
                      backgroundColor: themeColors.primary[100],
                      boxShadow: shadows.md,
                    },
                  },
                  '&:not(.Joy-selected):hover': {
                    backgroundColor: themeColors.neutral[100],
                  },
                  transition: 'all 0.15s ease',
                }}
              >
                <ListItemDecorator>
                  <DashboardIcon sx={{ fontSize: 20 }} />
                </ListItemDecorator>
                {!collapsed && <ListItemContent>All Workspaces</ListItemContent>}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </List>
      </Box>

      <Box sx={{ mx: 2, height: '1px', backgroundColor: themeColors.neutral[100] }} />

      {/* Workspaces Section */}
      <Box sx={{ px: collapsed ? 1 : 2, pt: 2, pb: 1 }}>
        {!collapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, mb: 1 }}>
            <Typography level="body-xs" sx={{ fontWeight: 700, textTransform: 'uppercase', color: themeColors.neutral[600], letterSpacing: '0.5px' }}>
              Workspaces
            </Typography>
            <Tooltip title="New Workspace">
              <IconButton
                size="sm"
                variant="plain"
                onClick={onCreateWorkspace}
                sx={{
                  '--IconButton-size': '24px',
                  color: themeColors.neutral[600],
                  '&:hover': {
                    backgroundColor: themeColors.primary[50],
                    color: themeColors.primary[600],
                  },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {collapsed && (
          <Tooltip title="New Workspace" placement="right">
            <IconButton
              size="sm"
              onClick={onCreateWorkspace}
              sx={{
                width: '100%',
                mb: 1,
                backgroundColor: themeColors.neutral[50],
                border: `2px dashed ${themeColors.neutral[300]}`,
                '&:hover': {
                  backgroundColor: themeColors.primary[50],
                  borderColor: themeColors.primary[400],
                  color: themeColors.primary[600],
                },
                transition: 'all 0.2s ease',
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Workspace List */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: collapsed ? 1 : 2 }}>
        <List size="sm" sx={{ '--List-gap': '6px', '--ListItem-radius': '10px' }}>
          {workspaces.slice(0, collapsed ? 10 : undefined).map((workspace) => (
            <ListItem key={workspace.id}>
              <Tooltip title={collapsed ? workspace.name : ""} placement="right">
                <ListItemButton
                  selected={selectedWorkspace?.id === workspace.id}
                  onClick={() => onWorkspaceSelect(workspace)}
                  sx={{
                    py: 1.5,
                    px: collapsed ? 0 : 1.5,
                    border: 'none',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    position: 'relative',
                    '&.Joy-selected': {
                      backgroundColor: themeColors.primary[50],
                      boxShadow: shadows.sm,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '20%',
                        bottom: '20%',
                        width: 3,
                        backgroundColor: themeColors.primary[500],
                        borderRadius: '0 4px 4px 0',
                      },
                      '&:hover': {
                        backgroundColor: themeColors.primary[100],
                        boxShadow: shadows.md,
                      },
                    },
                    '&:not(.Joy-selected):hover': {
                      backgroundColor: themeColors.neutral[100],
                    },
                    transition: 'all 0.15s ease',
                  }}
                >
                  <ListItemDecorator sx={{ minWidth: collapsed ? 'auto' : 40 }}>
                    {workspace.logo_url || workspace.logo ? (
                      <Box
                        component="img"
                        src={workspace.logo_url || workspace.logo}
                        alt={workspace.name}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 2,
                          objectFit: 'cover',
                          boxShadow: selectedWorkspace?.id === workspace.id ? shadows.glow : shadows.sm,
                          transition: 'all 0.2s ease',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 2,
                          background: `linear-gradient(135deg, ${workspace.main_color || themeColors.primary[500]}, ${workspace.main_color || themeColors.primary[600]})`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          boxShadow: selectedWorkspace?.id === workspace.id ? shadows.glow : shadows.sm,
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {workspace.name[0]?.toUpperCase()}
                      </Box>
                    )}
                  </ListItemDecorator>

                  {!collapsed && (
                    <ListItemContent>
                      <Box>
                        <Typography
                          level="title-sm"
                          sx={{
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: selectedWorkspace?.id === workspace.id ? themeColors.primary[700] : themeColors.neutral[900],
                          }}
                        >
                          {workspace.name}
                        </Typography>
                        <Typography
                          level="body-xs"
                          sx={{
                            color: themeColors.neutral[600],
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {workspace.project_count || 0} flows
                        </Typography>
                      </Box>
                    </ListItemContent>
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>
      </Box>

      <Box sx={{ mx: 2, height: '1px', backgroundColor: themeColors.neutral[100] }} />

      {/* Settings */}
      <Box sx={{ p: collapsed ? 1 : 2 }}>
        <Tooltip title={collapsed ? "Settings" : ""} placement="right">
          <IconButton
            size="sm"
            onClick={() => navigate('/settings')}
            sx={{
              width: '100%',
              py: 1.5,
              backgroundColor: themeColors.neutral[50],
              border: 'none',
              boxShadow: shadows.sm,
              '&:hover': {
                backgroundColor: themeColors.primary[50],
                color: themeColors.primary[600],
                boxShadow: shadows.md,
              },
              transition: 'all 0.2s ease',
            }}
          >
            <SettingsIcon fontSize="small" />
            {!collapsed && (
              <Typography level="body-sm" sx={{ ml: 1.5, fontWeight: 500 }}>
                Settings
              </Typography>
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
